import {
    Button,
    Card,
    Col,
    Container,
    ListGroup,
    ListGroupItem,
    Pagination,
    Row,
} from "react-bootstrap";
import { AppAuthStateProps } from "../../types/propTypes";

import "./Trackboard.css";

import { TrackboardItem } from "./TrackboardItem";

import {
    faChevronDown,
    faChevronLeft,
    faChevronUp,
    faEye,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import pLimit from "p-limit";
import queryString from "query-string";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    FhirPatientVisit,
    callFhirPatient,
} from "../../amplify/appsync/fetch-sync-fhir";
import {
    AFIB_GET_PATIENTS,
    Favorite,
    Note,
    callQuery
} from "../../amplify/appsync/query";
import {
    PatientVisit,
    PatientVisitMetadata,
} from "../../amplify/appsync/search-patients";
import { isDemoUser } from "../../utils/demo";
import { getStorage } from "../../utils/storage";
import { getSqlDateStrFromDateStr } from "../../utils/time";
import { useGlobalOptions } from "../common/GlobalOptionsContext";
import { DateType } from "../filters/dateTypes";
import { AfibFilterBar } from "../ops/AfibFilterBar";
import { FavoriteColumnSelectModal } from "./FavoriteColumnSelectModal";
import { FavoriteContextMenu } from "./FavoriteContextMenu";
import { FavoriteNewListModal } from "./FavoriteNewListModal";
import {
    AFIB_COLUMNS,
    formatCsnToFavorites,
    getDefaultAugmentedPatient,
    getDefaultColumns,
    getDemoMrnToName,
    sortPatientsByCol,
    updatePatientWithMrnToName
} from "./trackboardUtils";

const MAX_RESULTS_TO_DISPLAY = 20;

export const TrackboardAfib = (props: AppAuthStateProps) => {
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [sortColId, setSortColId] = useState<string>();
    const [sortColAsc, setSortColAsc] = useState<boolean>(true);
    const [patients, setPatients] = useState<
        (PatientVisit & FhirPatientVisit & PatientVisitMetadata)[]
    >([]);
    const [mrnToName, setMrnToName] = useState<any>({});
    const [csnToFavorites, setCsnToFavorites] = useState<any>({});
    const [csnToReviewed, setCsnToReviewed] = useState<any>({});
    const [mrnToPastFavorites, setMrnToPastFavorites] = useState<any>({});
    const [mrnToPastReviewed, setMrnToPastReviewed] = useState<any>({});
    const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
    const [contextMenu, setContextMenu] = useState<any>({
        x: 0,
        y: 0,
        csn: null,
    });
    const [showNewList, setShowNewList] = useState<boolean>(false);
    const [showColumnSelect, setShowColumnSelect] = useState<boolean>(false);
    const [favoriteLists, setFavoriteLists] = useState<string[]>([]);

    const [activePageNumber, setActivePageNumber] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalNumberOfResults, setTotalNumberOfResults] = useState<number>(0);

    const [columns, setColumns] = useState<any[]>(
        getStorage("afib_columns")
            ? // @ts-ignore
              JSON.parse(getStorage("afib_columns"))
            : AFIB_COLUMNS
    );

    const location = useLocation();
    const queryModel = queryString.parse(location.search);
    const { getDerivedRole } = useGlobalOptions();

    const [filterOptions, setFilterOptions]: any = useState<any>({
        prev_criteria: [],
        potential_ac_candidate: false,
        strong_ac_candidate: false,
    });

    const [filterDateOptions, setFilterDateOptions]: any = useState<any>({
        dateOptionType: DateType.Relative,
        relativeStart: 7,
        relativeStartScale: "d",
        relativeEnd: 0,
        relativeEndScale: "d",
        absoluteStart: queryModel["start"]
            ? new Date(Number(queryModel["start"]))
            : new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 7),
        absoluteEnd: queryModel["end"]
            ? new Date(Number(queryModel["end"]))
            : new Date(),
    });

    const sortPatients = (col: string) => {
        setPatients((activePts: any) => {
            const pts = [...activePts];
            let asc = sortColAsc;
            if (sortColId === col) {
                if (sortColAsc === false) {
                    // Reset to original state
                    col = "bed";
                    setSortColId("bed");
                    setSortColAsc(true);
                    asc = true;
                } else {
                    asc = false;
                    setSortColAsc(asc);
                }
            } else {
                setSortColId(col);
                setSortColAsc(true);
            }

            return sortPatientsByCol(pts, col, asc, props.tenant);
        });
    };

    const loadPatientNames = async (patients: PatientVisit[]) => {
        const limit = pLimit(2);
        const localMrnToName: any = {
            ...mrnToName,
        };

        const promises: any[] = [];
        for (let patient of patients) {
            const mrn = patient.mrn;
            if (!(mrn in localMrnToName)) {
                promises.push(
                    limit(() => {
                        return new Promise((resolve: any, reject: any) => {
                            callFhirPatient({
                                mrn: patient.mrn,
                            })
                                .then((pt: FhirPatientVisit) => {
                                    localMrnToName[patient.mrn] = pt;
                                    // Update the names slowly, to avoid hogging the bandwidth
                                    // which will prevent users from clicking on the patients
                                    if (
                                        Object.keys(localMrnToName).length %
                                            10 ===
                                        0
                                    ) {
                                        setMrnToName({
                                            ...localMrnToName,
                                        });
                                    }
                                    resolve(pt);
                                })
                                .catch((err: any) => {
                                    reject({});
                                });
                        });
                    })
                );
            }
        }

        const result = await Promise.all(promises);
        for (let fhirPatientInfo of result) {
            localMrnToName[fhirPatientInfo.mrn] = fhirPatientInfo;
        }
        setMrnToName(localMrnToName);
    };

    const refreshBaseData = () => {
        setIsLoading(true);
        callQuery({
            operation: AFIB_GET_PATIENTS,
            limit: MAX_RESULTS_TO_DISPLAY,
            offset: 20 * activePageNumber - 20,
            sort: "arrival_time",
            sort_asc: false,
            filters: {
                start: getSqlDateStrFromDateStr(
                    filterDateOptions.absoluteStart
                ),
                end: getSqlDateStrFromDateStr(filterDateOptions.absoluteEnd),
                ...(filterOptions["prev_criteria"].length > 0 && {
                    prev_criteria: filterOptions["prev_criteria"],
                }),
                strong_ac_candidate: filterOptions["strong_ac_candidate"],
                potential_ac_candidate: filterOptions["potential_ac_candidate"],
            },
        }).then((patients: PatientVisit[]) => {
            if (!isDemoUser(props.user, props.tenant)) {
                loadPatientNames(patients).then(() => {});
            } else {
                setMrnToName(getDemoMrnToName(patients));
            }
            const augmentedPatients = patients.map((p: PatientVisit) => {
                return getDefaultAugmentedPatient(p);
            });

            if (patients && patients.length > 0) {
                // @ts-ignore
                setTotalNumberOfResults(patients[0].full_count);
                const totalPages = Math.ceil(
                    // @ts-ignore
                    patients[0].full_count / MAX_RESULTS_TO_DISPLAY
                );
                setTotalPages(totalPages);
            } else {
                setTotalPages(0);
                setTotalNumberOfResults(0);
            }
            setPatients(augmentedPatients);
            setIsLoading(false);
        });
    };

    useEffect(() => {
        refreshBaseData();
    }, [activePageNumber]);

    useEffect(() => {
        setPatients((pts: any) => {
            return [
                ...pts.map(
                    (
                        patient: PatientVisit &
                            FhirPatientVisit &
                            PatientVisitMetadata
                    ) => {
                        return updatePatientWithMrnToName(patient, mrnToName);
                    }
                ),
            ];
        });
        setPatients((pts: any) => {
            return [
                ...pts.map(
                    (
                        patient: PatientVisit &
                            FhirPatientVisit &
                            PatientVisitMetadata
                    ) => {
                        return updatePatientWithMrnToName(patient, mrnToName);
                    }
                ),
            ];
        });
    }, [mrnToName]);

    const handleClear = () => {
        setShowContextMenu(false);
    };

    const updateCsnToFavorites = (
        csn: string,
        favoriteType: string,
        favorite?: Favorite
    ) => {
        console.log("updateCsnToFavorites", favoriteType);
        const map: any = { ...csnToFavorites };
        formatCsnToFavorites(map, csn, favoriteType, favorite);
        setCsnToFavorites(map);
    };

    const updatePatientWithNote = (note: Note) => {
        setPatients((activePatients) =>
            activePatients.map((pt) =>
                pt.csn === note.csn ? { ...pt, note: note.note } : pt
            )
        );
    };

    const range = (start: number, stop: number, step: number) => {
        if (typeof stop == "undefined") {
            // one param defined
            stop = start;
            start = 0;
        }

        if (typeof step == "undefined") {
            step = 1;
        }

        if ((step > 0 && start >= stop) || (step < 0 && start <= stop)) {
            return [];
        }

        var result = [];
        for (var i = start; step > 0 ? i < stop : i > stop; i += step) {
            result.push(i);
        }

        return result;
    };

    const pageUpdate = (nextPageInput: number) => {
        let nextPage = nextPageInput;
        nextPage = Math.min(totalPages, nextPage);
        nextPage = Math.max(1, nextPage);
        setActivePageNumber(nextPage);
    };

    const updateFilterOptions = (option: string, val: any) => {
        setFilterOptions({
            ...filterOptions,
            [option]: val,
        });
    };

    return (
        <div>
            <div
                className="trackboard-afib-sidebar"
                style={{
                    overflowY: "scroll",
                    marginTop: isDemoUser(props.user) ? "76px" : undefined,
                }}
            >
                <AfibFilterBar
                    filterOptions={filterOptions}
                    updateFilterOptions={updateFilterOptions}
                    isLoadingMetrics={isLoading}
                    filterDateOptions={filterDateOptions}
                    setFilterDateOptions={setFilterDateOptions}
                    refresh={refreshBaseData}
                />
            </div>
            <Container
                fluid
                className="trackboard-afib-container"
                style={{
                    marginTop: isDemoUser(props.user) ? "140px" : undefined,
                }}
                onClick={handleClear}
            >
                <Row style={{ marginBottom: "12px" }}>
                    <Col md={12}>
                        <div style={{ width: "100%", verticalAlign: "middle" }}>
                            <Button
                                style={{ float: "left" }}
                                size="sm"
                                variant={"outline-secondary"}
                                className="trackboard-quick-actions-btn"
                                onClick={() => {
                                    navigate("/trackboard-v2");
                                }}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />{" "}
                                Trackboard
                            </Button>{" "}
                            <h5
                                style={{
                                    float: "left",
                                    marginTop: "4px",
                                    marginLeft: "16px",
                                }}
                            >
                                Afib Patients ({totalNumberOfResults})
                            </h5>
                        </div>
                    </Col>
                </Row>

                <Row style={{ marginBottom: "24px" }}>
                    <Col>
                        <Card className="trackboard-table">
                            <ListGroup variant="flush">
                                <ListGroupItem className="d-flex flex-column trackboard-table-header">
                                    <div
                                        style={{
                                            display: "block",
                                            width: "100%",
                                        }}
                                    >
                                        {columns.map((col) => {
                                            const totalRelativeCols =
                                                columns.reduce(
                                                    (acc, obj) =>
                                                        acc + (obj.size ?? 0),
                                                    0
                                                );
                                            if (col.id === "action") {
                                                return (
                                                    <div
                                                        key={col.id}
                                                        className="text-right"
                                                        style={{
                                                            display:
                                                                "inline-block",
                                                            width: `${
                                                                (100 *
                                                                    col.size) /
                                                                totalRelativeCols
                                                            }%`,
                                                        }}
                                                    >
                                                        <Button
                                                            variant="primary"
                                                            className={
                                                                "trackboard-show-btn"
                                                            }
                                                            onClick={() =>
                                                                setShowColumnSelect(
                                                                    true
                                                                )
                                                            }
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faEye}
                                                            />
                                                        </Button>
                                                    </div>
                                                );
                                            } else {
                                                return (
                                                    <div
                                                        key={col.id}
                                                        style={{
                                                            display:
                                                                "inline-block",
                                                            cursor: "pointer",
                                                            width: `${
                                                                (100 *
                                                                    col.size) /
                                                                totalRelativeCols
                                                            }%`,
                                                        }}
                                                        onClick={() =>
                                                            sortPatients(col.id)
                                                        }
                                                    >
                                                        {col.name}
                                                        {sortColId ===
                                                        col.id ? (
                                                            sortColAsc ? (
                                                                <>
                                                                    {" "}
                                                                    <FontAwesomeIcon
                                                                        icon={
                                                                            faChevronDown
                                                                        }
                                                                    />
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {" "}
                                                                    <FontAwesomeIcon
                                                                        icon={
                                                                            faChevronUp
                                                                        }
                                                                    />
                                                                </>
                                                            )
                                                        ) : (
                                                            <></>
                                                        )}
                                                    </div>
                                                );
                                            }
                                        })}
                                    </div>
                                </ListGroupItem>
                                {isLoading ? (
                                    <>
                                        {[1, 2, 3].map((v) => (
                                            <TrackboardItem
                                                key={v}
                                                /** @ts-ignore */
                                                patient={null}
                                                columns={columns}
                                                csnToFavorites={csnToFavorites}
                                                csnToReviewed={csnToReviewed}
                                                pastFavorites={[]}
                                                pastReviewed={[]}
                                                setCsnToReviewed={
                                                    setCsnToReviewed
                                                }
                                                setShowContextMenu={
                                                    setShowContextMenu
                                                }
                                                setContextMenu={setContextMenu}
                                                padNotes={true}
                                                isPlaceholder={true}
                                                updatePatientWithNote={
                                                    updatePatientWithNote
                                                }
                                                tenant={props.tenant}
                                                user={props.user}
                                            />
                                        ))}
                                    </>
                                ) : (
                                    <>
                                        {patients.map((pt) => (
                                            <TrackboardItem
                                                key={pt.csn}
                                                patient={pt}
                                                columns={columns}
                                                csnToFavorites={csnToFavorites}
                                                csnToReviewed={csnToReviewed}
                                                pastFavorites={
                                                    mrnToPastFavorites[pt.mrn]
                                                }
                                                pastReviewed={
                                                    mrnToPastReviewed[pt.mrn]
                                                }
                                                setCsnToReviewed={
                                                    setCsnToReviewed
                                                }
                                                padNotes={true}
                                                setShowContextMenu={
                                                    setShowContextMenu
                                                }
                                                setContextMenu={setContextMenu}
                                                updatePatientWithNote={
                                                    updatePatientWithNote
                                                }
                                                tenant={props.tenant}
                                                user={props.user}
                                            />
                                        ))}
                                    </>
                                )}
                            </ListGroup>
                        </Card>
                    </Col>
                </Row>
                <FavoriteContextMenu
                    show={showContextMenu}
                    contextMenu={contextMenu}
                    lists={favoriteLists}
                    setShow={setShowContextMenu}
                    setShowNewList={setShowNewList}
                    updateCsnToFavorites={updateCsnToFavorites}
                />
                <FavoriteNewListModal
                    show={showNewList}
                    setShow={setShowNewList}
                    lists={favoriteLists}
                    setLists={setFavoriteLists}
                    contextMenu={contextMenu}
                    updateCsnToFavorites={updateCsnToFavorites}
                />
                <FavoriteColumnSelectModal
                    show={showColumnSelect}
                    setShow={setShowColumnSelect}
                    columns={columns}
                    setColumns={setColumns}
                    allColumns={getDefaultColumns(getDerivedRole(props.user))}
                    saveKey="trackboard_columns"
                />

                <Row>
                    <Col>
                        <Pagination>
                            <Pagination.First
                                onClick={() => setActivePageNumber(1)}
                            />
                            <Pagination.Prev
                                onClick={() =>
                                    setActivePageNumber(
                                        Math.max(1, activePageNumber - 1)
                                    )
                                }
                            />

                            {totalPages < 6 ? (
                                <>
                                    {range(1, totalPages + 1, 1).map(
                                        (idx: number) => {
                                            if (idx === activePageNumber)
                                                return (
                                                    <Pagination.Item
                                                        key={idx}
                                                        active
                                                        onClick={() =>
                                                            pageUpdate(idx)
                                                        }
                                                    >
                                                        {idx}
                                                    </Pagination.Item>
                                                );
                                            else
                                                return (
                                                    <Pagination.Item
                                                        key={idx}
                                                        onClick={() =>
                                                            pageUpdate(idx)
                                                        }
                                                    >
                                                        {idx}
                                                    </Pagination.Item>
                                                );
                                        }
                                    )}
                                </>
                            ) : (
                                <>
                                    {activePageNumber - 4 > 1 && (
                                        <Pagination.Ellipsis />
                                    )}
                                    {range(
                                        Math.max(1, activePageNumber - 4),
                                        Math.min(
                                            totalPages,
                                            activePageNumber + 5
                                        ),
                                        1
                                    ).map((idx: number) => {
                                        if (idx === activePageNumber)
                                            return (
                                                <Pagination.Item
                                                    key={idx}
                                                    active
                                                    onClick={() =>
                                                        pageUpdate(idx)
                                                    }
                                                >
                                                    {idx}
                                                </Pagination.Item>
                                            );
                                        else
                                            return (
                                                <Pagination.Item
                                                    key={idx}
                                                    onClick={() =>
                                                        pageUpdate(idx)
                                                    }
                                                >
                                                    {idx}
                                                </Pagination.Item>
                                            );
                                    })}
                                    {activePageNumber + 5 < totalPages + 1 && (
                                        <Pagination.Ellipsis />
                                    )}
                                </>
                            )}
                            <Pagination.Next
                                onClick={() =>
                                    pageUpdate(
                                        Math.min(
                                            totalPages,
                                            activePageNumber + 1
                                        )
                                    )
                                }
                            />
                            <Pagination.Last
                                onClick={() =>
                                    pageUpdate(Math.max(totalPages, 1))
                                }
                            />
                        </Pagination>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};
